import React, { Component } from 'react';
import { View, ViewPropTypes, TouchableOpacity, Platform, Dimensions } from 'react-native'
import BackgroundTimer from 'react-native-background-timer';

import { TextPropTypes } from 'deprecated-react-native-prop-types';
import moment from 'moment';
import PropTypes from 'prop-types';

import FoundationTestKeys from '../../FoundationTestKeys';
import { DateCurrencyFromatter } from '../../Utils';
import { Icon } from '../Icon';
import Shimmer from '../Shimmer';
import { withTheme, LightThemeColors } from '../Themes';
import VFText from '../VFText';

import styles from './ManualRefresh.Styles';

class ManualRefresh extends Component {
  constructor(props) {
    super(props);

    this.state = {
      updateTime: new Date(),
      lastUpdate: 0,
      isLoading: true,
    };
  }

  componentDidMount() {
    this.setTimeUpdateInterval(this.props.lastUpdateTime);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.lastUpdateTime !== this.props.lastUpdateTime) {
      this.setTimeUpdateInterval(this.props.lastUpdateTime);
    }
  }

  componentWillUnmount() {
    BackgroundTimer.clearInterval(this.lastUpdateInterval);
  }

  getUpdateTextProps() {
    const { isLoading, lastUpdate, updateTime } = this.state;

    const {
      refreshTitleStatusKeys: {
        updating = 'lastupdatedlabel_updating',
        updatedNow = 'lastupdatedlabel_justupdated',
        sinceLastUpdate = 'lastupdatedlabel_minute',
      } = {},
    } = this.props;
    const dateFormatter = DateCurrencyFromatter.dateCurrencyFromatter;
    if (isLoading) {
      return { i18nKey: updating };
    }

    switch (true) {
      // Less than a minute
      case lastUpdate < 1:
        return { i18nKey: updatedNow };
      // Less than 15 minutes
      case lastUpdate < 15:
        return {
          i18nKey: sinceLastUpdate,
          placeHolders: [lastUpdate],
        };
      // Less than a 24 hours and on the same day
      case lastUpdate < 60 * 24 && !this.isYesterday(updateTime):
        return {
          i18nKey: 'lastupdatedlabel_timestamp',
          placeHolders: [
            dateFormatter.dateTransformer(updateTime, dateFormatter.dateFormats.timeStamp),
          ],
        };
      // Not on the same day
      default:
        return {
          i18nKey: 'lastupdatedlabel_timestamp',
          placeHolders: [
            dateFormatter.dateTransformer(updateTime, dateFormatter.dateFormats.timeStampWithDate),
          ],
        };
    }
  }

  setTimeUpdateInterval = (updateTime = new Date()) => {
    BackgroundTimer.clearInterval(this.lastUpdateInterval);

    const lastUpdate = moment().diff(updateTime, 'minutes');

    this.setState({ isLoading: false, updateTime, lastUpdate }, () => {
      this.lastUpdateInterval = BackgroundTimer.setInterval(() => {
        this.setState({ lastUpdate: this.state.lastUpdate + 1 });
      }, 60000);
    });
  };

  refreshManually = () => {
    this.setState({ isLoading: true });

    this.props
      .onRefresh()
      .then(isRefreshed => {
        if (isRefreshed) {
          this.setTimeUpdateInterval();
          this.props.setLastUpdateTime();
        }
      })
      .catch(_ => { })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  };

  isYesterday(time) {
    const dateFormatter = DateCurrencyFromatter.dateCurrencyFromatter;
    const date = parseInt(
      dateFormatter.dateTransformer(time, dateFormatter.dateFormats.dayNumber),
      10,
    );
    const today = parseInt(
      dateFormatter.dateTransformer(new Date(), dateFormatter.dateFormats.dayNumber),
      10,
    );
    return today - date === 1;
  }

  renderShimmer() {
    return (
      <View style={styles.shimmerContainer} testID={FoundationTestKeys.manualRefreshShimmerView}>
        <Shimmer
          config={this.props.lastUpdatedLoadingConfig}
          shimmerPlaceholderConfig={this.props.shimmerPlaceholderConfig}
        />
      </View>
    );
  }

  renderUpdateComponent() {
    const { images, iconName, iconSize } = this.props;
    return (
      <>
        <VFText
          {...this.getUpdateTextProps()}
          style={[styles.lastUpdatedtext(this.props.theme), this.props.lastUpdatedTextStyle]}
          testKey={FoundationTestKeys.DBRefreshText}
        />

        <Icon
          name={images[iconName]}
          type="image"
          style={styles.iconStyle}
          accessibilityLabel={FoundationTestKeys.DBRefreshIcon}
          testID={FoundationTestKeys.DBRefreshIcon}
          size={iconSize}
        />
      </>
    );
  }

  render() {
    if (Platform.OS === 'web' && Dimensions.get('window').width > 700) {
      return (
        <View style={{ alignItems: 'flex-end' }}>
          <View style={{ maxWidth: '100px' }}>
            <TouchableOpacity
              onPress={this.refreshManually}
              style={[styles.containerStyle, this.props.containerStyle]}
              accessible={false}
            >
              <View style={styles.headerThirdRow} accessible={false}>
                {this.props.isLoading
                  ? this.renderShimmer()
                  : this.renderUpdateComponent()}
              </View>
            </TouchableOpacity>
          </View>
        </View>
      )
    }
    return (
      <TouchableOpacity
        onPress={this.refreshManually}
        style={[styles.containerStyle, this.props.containerStyle]}
        accessible={false}
        testID={FoundationTestKeys.manualRefreshTO}
      >
        <View
          style={[styles.headerThirdRow, this.props.refreshViewStyle]}
          accessible={false}
          testID={FoundationTestKeys.manualRefreshTOView}
        >
          {this.props.isLoading ? this.renderShimmer() : this.renderUpdateComponent()}
        </View>
      </TouchableOpacity>
    );
  }
}

ManualRefresh.defaultProps = {
  images: {},
  onRefresh: () => Promise.reject(),
  setLastUpdateTime: () => { },
  theme: LightThemeColors,
  iconName: 'ic_refresh',
  iconSize: 16,
};

ManualRefresh.propTypes = {
  images: PropTypes.object,
  onRefresh: PropTypes.func,
  lastUpdateTime: PropTypes.instanceOf(Date),
  setLastUpdateTime: PropTypes.func,
  lastUpdatedLoadingConfig: PropTypes.array,
  shimmerPlaceholderConfig: PropTypes.object,
  isLoading: PropTypes.bool,
  theme: PropTypes.shape({
    name: PropTypes.string,
    isDark: PropTypes.bool,
    colors: PropTypes.object,
  }),
  refreshTitleStatusKeys: PropTypes.shape({
    updatedNow: PropTypes.string,
    sinceLastUpdate: PropTypes.string,
    updating: PropTypes.string,
  }),
  containerStyle: ViewPropTypes.style,
  lastUpdatedTextStyle: TextPropTypes.style,
  refreshViewStyle: ViewPropTypes.style,
  iconName: PropTypes.string,
  iconSize: PropTypes.number,
};

export default withTheme(ManualRefresh);
